import { EditOutlined } from "@ant-design/icons";
import { Form, message, Modal, Select, Tooltip } from "antd";
import { useForm } from "antd/es/form/Form";
import { Button } from "antd/lib";
import { getAllPaymentMethods } from "hooks/queries/payment-methods";
import { useUpdateCustomerPackage } from "hooks/mutations/customerpackage";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { useState } from "react";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";

interface UpdateCustomerPackagePaymentMethodModalProps {
  packageId: string;
  currentPaymentMethodId: string | null;
  /** The recorded method's name, so a method deleted since the sale can
   *  still be shown as the selected option (System Review S7). */
  currentPaymentMethodName?: string | null;
  /** A reward credit was never paid for, so there is no payment method to
   *  record. Greyed out with no popover, like a restricted control. */
  disabled?: boolean;
}

// Editable any time after a purchase (unlike the credits/expiry-date modals,
// validity never locks it): the payment method can be corrected whenever,
// and every change is recorded on the "Riwayat Metode Pembayaran" tab
// instead of being locked. The one exception is `disabled`, for a reward
// credit, which had no payment at all.
const UpdateCustomerPackagePaymentMethodModal = ({
  packageId,
  currentPaymentMethodId,
  currentPaymentMethodName,
  disabled = false,
}: UpdateCustomerPackagePaymentMethodModalProps) => {
  const [form] = useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: paymentMethods = [] } = getAllPaymentMethods();
  const { mutate: updateCustomerPackage } = useUpdateCustomerPackage();

  // The picker lists the studio's LIVE methods. A sale keeps pointing at a
  // method deleted since, so the recorded one is appended when it is missing
  // from that list; otherwise the field opened blank and read as "no method
  // was ever set". It is shown, not offered: muted name plus a Dihapus chip
  // (colour is never the only cue) and `disabled`, so it stays readable as
  // the current value but cannot be picked again once the admin moves off it.
  const recordedIsRetired =
    !!currentPaymentMethodId &&
    !paymentMethods.some((method) => method.id === currentPaymentMethodId);
  const options: { value: string; label: React.ReactNode; disabled?: boolean }[] =
    paymentMethods.map((method) => ({ value: method.id, label: method.name }));
  if (recordedIsRetired) {
    options.push({
      value: currentPaymentMethodId!,
      disabled: true,
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: colors.ink3 }}>{currentPaymentMethodName ?? "-"}</span>
          <StatusBadge tone="neutral" label={translate("paymentMethods.retired_label")} dot={false} />
        </span>
      ),
    });
  }

  // Seed the field from the CURRENT record on every open. `initialValues`
  // only applies on first mount and `resetFields` on close ran before the
  // package query had refetched, so the picker reopened on the previous
  // method after a change (Andrew, 2026-09-30).
  const openModal = () => {
    form.setFieldsValue({ payment_method_id: currentPaymentMethodId ?? undefined });
    setIsModalOpen(true);
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values: { payment_method_id?: string }) => {
        // allowClear leaves this undefined when cleared — normalize to
        // null so the backend sees an explicit clear, not "unchanged".
        const next = values.payment_method_id ?? null;
        // Nothing changed: nothing to save. Also what keeps a retired
        // method's id out of the request, which the server would refuse.
        if (next !== currentPaymentMethodId) {
          updateCustomerPackage({ packageId, payment_method_id: next });
        }
        setIsModalOpen(false);
      })
      .catch(() => {
        message.error(translate("messages.error.invalid_form"));
        setIsModalOpen(false);
      });
  };

  if (disabled) {
    return <Button color="primary" type="link" icon={<EditOutlined />} disabled />;
  }

  return (
    <>
      <Tooltip title={translate("popover.edit_payment_method")}>
        <Button color="primary" type="link" icon={<EditOutlined />} onClick={openModal} />
      </Tooltip>
      <Modal
        title={translate("title.update_payment_method")}
        open={isModalOpen}
        onOk={onSubmit}
        onCancel={() => setIsModalOpen(false)}
        okText={translate("button.confirm")}
      >
        <Form form={form} layout="vertical" name="updatePaymentMethod">
          <Form.Item
            name="payment_method_id"
            label={translate("paymentMethods.col_payment_method")}
            extra={recordedIsRetired ? translate("paymentMethods.retired_hint") : undefined}
          >
            <Select
              allowClear
              placeholder={translate("paymentMethods.select_placeholder")}
              options={options}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UpdateCustomerPackagePaymentMethodModal;
